import type { CalculatorContent, Faq } from '@/types/calculator'
import { ANALYSIS_YEARS } from './calculation'
import type { SolarRoiInputs } from './types'

const GENERATION_PER_KW = 1650
const YEARS = ANALYSIS_YEARS

export const content: CalculatorContent<SolarRoiInputs> = {
  intro: `Estimate payback and solar ROI in South Africa with this solar payback calculator. Whether you are beating load shedding or cutting your municipal bill, enter your usage, tariff and system cost to see savings over ${YEARS} years.`,
  howItWorks: [
    `Annual generation is estimated at roughly ${GENERATION_PER_KW.toLocaleString('en-ZA')} kWh per installed kW of panels — a broad South African average. Only the share of generation you use during daylight hours (or store in a battery) offsets your bill.`,
    `Year-one savings are the usable generation multiplied by your tariff. Savings then escalate each year by your chosen tariff-escalation rate over a ${YEARS}-year analysis period.`,
    `Simple payback divides the total system cost by year-one savings — it does not account for escalation, so it is a conservative figure. The ${YEARS}-year ROI nets total escalated savings against the system cost.`,
    'If you finance the system, a monthly instalment is estimated with the standard amortising-loan formula. The uncertainty warning is deliberate: shading, orientation, equipment and tariffs all move the real outcome.',
  ],
  formulas: [
    {
      label: 'Total system cost',
      expression: 'system size (kW) × 1 000 × cost per watt',
    },
    {
      label: 'Annual generation',
      expression: `system size × ${GENERATION_PER_KW.toLocaleString('en-ZA')} kWh`,
    },
    {
      label: 'Year-1 savings',
      expression: 'annual generation × daytime usage share × tariff',
    },
    {
      label: 'Simple payback',
      expression: 'total system cost ÷ year-1 savings',
    },
    {
      label: `${YEARS}-year net savings`,
      expression: 'Σ escalating annual savings − total system cost',
    },
  ],
  example: {
    title: 'A 5 kW system on a 900 kWh/month household',
    description:
      'A household with average monthly usage installs a mid-size system paid in cash and wants the payback and long-term saving.',
    inputs: {
      monthlyUsageKwh: 900,
      tariffPerKwh: 3.5,
      daytimeUsageShare: 0.75,
      systemCostPerW: 15,
      systemSizeKw: 5,
      annualTariffEscalation: 0.08,
      financingYears: 0,
      financingRate: 0.12,
    },
  },
  definitions: [
    {
      term: 'kWp / system size',
      description:
        'The peak kilowatt rating of the solar array — the headline size installers quote.',
    },
    {
      term: 'Self-consumption',
      description:
        'The share of generated power you use directly. In South Africa most municipalities do not pay for exported power, so self-consumption drives the savings.',
    },
    {
      term: 'Payback period',
      description:
        'Years until cumulative savings equal the upfront cost. After payback, the savings are effectively free electricity.',
    },
    {
      term: 'Tariff escalation',
      description:
        'The annual percentage increase in electricity prices. Rising tariffs shorten the effective payback over time.',
    },
    {
      term: 'Load shedding',
      description:
        'Scheduled power outages — a major driver of solar adoption. This calculator values bill savings, not backup-power benefits.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'How accurate is the solar estimate?',
    answer:
      'It is a wide planning estimate only. Actual generation depends on roof direction, shading, inverter size, battery use, municipality tariffs and future tariff changes.',
  },
  {
    question: 'Does this include batteries for load shedding?',
    answer:
      'No. The estimate models bill savings from solar generation only — battery costs and backup value are outside its scope.',
  },
  {
    question: 'What payback period is typical in South Africa?',
    answer:
      'Estimates vary widely with tariff and self-consumption — many households see roughly 5–8 years, but your inputs decide the figure shown here.',
  },
  {
    question: 'Can I sell excess solar power back to the grid?',
    answer:
      'Some municipalities allow feed-in, but rules and rates differ. This calculator only counts the electricity you use yourself.',
  },
]
